import {
  getVegaLiteFieldBounds,
  VEGA_LITE_NUMBER_VALUE,
  VEGA_LITE_UNKNOWN_VALUE,
  type VegaLiteExpressionValueBounds,
} from './vega-lite-expression-allocation';
import {
  analyzeVegaLiteCalculateExpression,
  analyzeVegaLiteTupleExpression,
  describeVegaLiteExpressionFindings,
} from './vega-lite-expression-policy';
import { type JsonObject, type JsonValue } from './vega-lite-json';
import { VEGA_LITE_CALCULATE_LIMITS } from './vega-lite-resource-limits';
import { VEGA_LITE_DATASET_NAME } from './vega-lite-widget-options';

export type VegaLiteCalculateContext = {
  fields: Map<string, VegaLiteExpressionValueBounds>;
  budget: { allocation: number };
};

type AddIssue = (path: string, message: string) => void;

function reportExpressionAllocation(
  result: ReturnType<typeof analyzeVegaLiteTupleExpression>,
  path: string,
  context: VegaLiteCalculateContext,
  addIssue: AddIssue,
) {
  describeVegaLiteExpressionFindings(result.findings, VEGA_LITE_DATASET_NAME).forEach((message) =>
    addIssue(path, message),
  );
  context.budget.allocation += result.allocation;

  if (context.budget.allocation > VEGA_LITE_CALCULATE_LIMITS.maxAllocatedStringLength) {
    addIssue(
      path,
      `Expression string allocation across transforms and expression sites must not exceed ${VEGA_LITE_CALCULATE_LIMITS.maxAllocatedStringLength} code units per row, including intermediate values. Shorten labels or use numeric calculations.`,
    );
  }
}

export function validateVegaLiteTupleExpression(
  expression: string,
  path: string,
  context: VegaLiteCalculateContext,
  addIssue: AddIssue,
) {
  reportExpressionAllocation(
    analyzeVegaLiteTupleExpression(expression, context.fields),
    path,
    context,
    addIssue,
  );
}

const NUMERIC_OPERATIONS = new Set([
  'count',
  'valid',
  'missing',
  'distinct',
  'sum',
  'product',
  'mean',
  'average',
  'variance',
  'variancep',
  'stdev',
  'stdevp',
  'stderr',
  'median',
  'q1',
  'q3',
  'ci0',
  'ci1',
  'row_number',
  'rank',
  'dense_rank',
  'percent_rank',
  'cume_dist',
  'ntile',
]);
const FIELD_OPERATIONS = new Set([
  'min',
  'max',
  'first_value',
  'last_value',
  'nth_value',
  'lag',
  'lead',
]);
const FLAT_FIELD_NAME = /^[A-Za-z0-9_$]+$/;

function isObject(value: JsonValue | undefined): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOutputName(value: JsonValue): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    !['__proto__', 'constructor', 'prototype'].includes(value)
  );
}

function nullableFieldBounds(value: VegaLiteExpressionValueBounds): VegaLiteExpressionValueBounds {
  return value.kind === 'unknown'
    ? value
    : {
        kind: value.kind === 'string' ? 'scalar' : value.kind,
        maxStringLength: Math.max(9, value.maxStringLength),
      };
}

function trackFilterParsing(
  value: JsonValue,
  path: string,
  context: VegaLiteCalculateContext,
  addIssue: AddIssue,
) {
  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      trackFilterParsing(entry, `${path}/${index}`, context, addIssue),
    );
    return;
  }

  if (!isObject(value)) {
    return;
  }

  const sample =
    value.equal ??
    value.lt ??
    value.lte ??
    value.gt ??
    value.gte ??
    (Array.isArray(value.range) ? value.range[0] : undefined) ??
    (Array.isArray(value.oneOf) ? value.oneOf[0] : undefined) ??
    (Array.isArray(value.in) ? value.in[0] : undefined);
  const parsesDate = value.timeUnit !== undefined || isObject(sample);
  const parsesNumber = typeof sample === 'number';
  const parsesString = typeof sample === 'string';

  if (value.field !== undefined && (parsesDate || parsesNumber || parsesString)) {
    const field = value.field;

    if (!isOutputName(field) || !FLAT_FIELD_NAME.test(field)) {
      addIssue(
        `${path}/field`,
        'Implicit filter parsing requires a flat field name so calculate bounds cannot be bypassed by a renamed nested field.',
      );
    } else {
      const input = getVegaLiteFieldBounds(context.fields, field);

      // Vega-Lite may insert toString/toNumber/toDate and overwrite this field before a formula.
      // It can also skip parsing previously derived fields, so retain both possible value types.
      if (parsesDate || input.kind === 'unknown') {
        context.fields.set(field, VEGA_LITE_UNKNOWN_VALUE);
      } else if (parsesNumber && (input.kind === 'number' || input.kind === 'boolean')) {
        context.fields.set(field, VEGA_LITE_NUMBER_VALUE);
      } else {
        context.fields.set(field, {
          kind: 'scalar',
          maxStringLength: Math.max(input.maxStringLength, VEGA_LITE_NUMBER_VALUE.maxStringLength),
        });
      }
    }
  }

  for (const key of ['and', 'or', 'not']) {
    if (value[key] !== undefined) {
      trackFilterParsing(value[key], `${path}/${key}`, context, addIssue);
    }
  }
}

function trackTransformOutputs(
  transform: JsonObject,
  path: string,
  context: VegaLiteCalculateContext,
  addIssue: AddIssue,
) {
  const inputs = new Map(context.fields);

  if (transform.filter !== undefined) {
    trackFilterParsing(transform.filter, `${path}/filter`, context, addIssue);
  }

  if (transform.aggregate !== undefined) {
    context.fields.forEach((bounds, name) => {
      context.fields.set(name, nullableFieldBounds(bounds));
    });
  }

  for (const key of ['aggregate', 'joinaggregate', 'window']) {
    const definitions = transform[key];

    if (!Array.isArray(definitions)) {
      continue;
    }

    definitions.forEach((definition, index) => {
      if (!isObject(definition)) {
        return;
      }

      if (!isOutputName(definition.as)) {
        addIssue(
          `${path}/${key}/${index}/as`,
          'Transform outputs require an explicit, safe field name for calculate allocation analysis.',
        );
        return;
      }

      const { op, field } = definition;
      let bounds: VegaLiteExpressionValueBounds = VEGA_LITE_UNKNOWN_VALUE;

      if (typeof op === 'string' && NUMERIC_OPERATIONS.has(op)) {
        bounds = VEGA_LITE_NUMBER_VALUE;
      } else if (
        typeof op === 'string' &&
        FIELD_OPERATIONS.has(op) &&
        typeof field === 'string' &&
        FLAT_FIELD_NAME.test(field)
      ) {
        bounds = nullableFieldBounds(getVegaLiteFieldBounds(inputs, field));
      }

      context.fields.set(definition.as, bounds);
    });
  }

  if (transform.timeUnit !== undefined) {
    if (!isOutputName(transform.as)) {
      addIssue(`${path}/as`, 'Time unit transforms require one explicit, safe output field name.');
    } else {
      context.fields.set(transform.as, VEGA_LITE_UNKNOWN_VALUE);
      context.fields.set(`${transform.as}_end`, VEGA_LITE_UNKNOWN_VALUE);
    }

    if (!isOutputName(transform.field) || !FLAT_FIELD_NAME.test(transform.field)) {
      addIssue(
        `${path}/field`,
        'Time unit parsing requires a flat source field name for calculate allocation analysis.',
      );
    } else if (!inputs.has(transform.field)) {
      context.fields.set(transform.field, VEGA_LITE_UNKNOWN_VALUE);
    }

    return;
  }

  if (transform.bin === undefined && transform.stack === undefined) {
    return;
  }

  if (Array.isArray(transform.as) && transform.as.length !== 2) {
    addIssue(`${path}/as`, 'Bin and stack transforms require exactly two output fields.');
    return;
  }

  const names = Array.isArray(transform.as) ? transform.as : [transform.as];

  if (!names.every(isOutputName)) {
    addIssue(
      `${path}/as`,
      'Transform outputs require explicit, safe field names for calculate allocation analysis.',
    );
    return;
  }

  if (
    transform.bin !== undefined &&
    typeof transform.bin !== 'boolean' &&
    !isObject(transform.bin)
  ) {
    addIssue(`${path}/bin`, 'Bin transforms require a boolean or bin parameter object.');
    return;
  }

  if (transform.stack !== undefined && !isOutputName(transform.stack)) {
    addIssue(`${path}/stack`, 'Stack transforms require an explicit, safe source field name.');
    return;
  }

  names.forEach((name) => context.fields.set(name, VEGA_LITE_NUMBER_VALUE));

  if (typeof transform.as === 'string') {
    context.fields.set(`${transform.as}_end`, VEGA_LITE_NUMBER_VALUE);
  }
}

/**
 * Siblings get independent field maps; all arrays share a cumulative per-row allocation budget.
 * Never reset a derived or unknown field to the prepared-input ceiling after another transform.
 */
export function validateVegaLiteCalculateTransforms(
  transforms: readonly JsonValue[],
  path: string,
  context: VegaLiteCalculateContext,
  addIssue: AddIssue,
  visitTransform?: (transform: JsonObject, path: string, context: VegaLiteCalculateContext) => void,
) {
  transforms.forEach((transform, index) => {
    if (!isObject(transform)) {
      return;
    }

    const transformPath = `${path}/${index}`;

    if (transform.calculate === undefined) {
      trackTransformOutputs(transform, transformPath, context, addIssue);
      visitTransform?.(transform, transformPath, context);
      return;
    }

    visitTransform?.(transform, transformPath, context);

    if (!isOutputName(transform.as)) {
      addIssue(`${transformPath}/as`, 'Calculate requires an explicit, safe output field name.');
    }

    const result =
      typeof transform.calculate === 'string'
        ? analyzeVegaLiteCalculateExpression(transform.calculate, context.fields)
        : undefined;

    if (result === undefined) {
      addIssue(
        `${transformPath}/calculate`,
        'Calculate must be a primitive Vega expression string.',
      );
    } else {
      reportExpressionAllocation(result, `${transformPath}/calculate`, context, addIssue);
    }

    if (isOutputName(transform.as)) {
      context.fields.set(transform.as, result?.value ?? VEGA_LITE_UNKNOWN_VALUE);
    }
  });
}
